import { UTCDateMini } from "@date-fns/utc"
import { Export, Factory, ListNumbers, PencilSimple, Yarn } from "@phosphor-icons/react"
import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router"
import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  isBefore,
  subDays,
  subMonths,
} from "date-fns"
import download from "downloadjs"
import { utils, write } from "xlsx"
import { capitalize, maxBy, minBy, sum } from "lodash"
import { groupBy, mapBy, sumBy } from "~/utils/tsUtils"
import { appendSheet, sheetFromRows, XLSX_CONTENT_TYPE } from "~/utils/excelSheet"
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react"
import { toast } from "react-toastify"
import { AddVariantsToPurchasePlanDialog } from "~/components/purchase-plan/AddVariantsToPurchasePlanDialog"
import CollectionInformationDialog from "~/components/purchase-plan/CollectionInformationDialog"
import { BuySettingsDialog } from "~/components/purchase-plan/BuySettingsDialog"
import { SaveSnapshotDialog } from "~/components/purchase-plan/SaveSnapshotDialog"
import { FashionBuyPlanExportMenu } from "~/components/purchase-plan/FashionBuyPlanExportMenu"
import { SyncPurchasePlanWithCollectionDialog } from "~/components/purchase-plan/SyncPurchasePlanWithCollectionDialog"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { ManagePurchasePlanDialog } from "~/components/reusable/dialogs/ManagePurchasePlanDialog"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import { Module } from "~/components/design-system/pages"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import NotFound from "~/components/reusable/NotFound"
import { SupplyPlanProvider, SupplyPlanVariant } from "~/context/SupplyPlanProvider"
import { useFilters } from "~/hooks/useFilters"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import { useSavePurchasePlanSnapshot } from "~/hooks/usePurchasePlan"
import { useStockLevels } from "~/hooks/useVariantTableHooks"
import { useVariantsByIds } from "~/hooks/useVariants"
import { usePrimaryWarehouses } from "~/hooks/useWarehouses"
import { type FashionWarehousePurchaseOrder } from "~/hooks/useFashionSkuRows"
import { type ISuggestedOrder } from "~/models/suggestedOrder"
import { api } from "~/utils/api"
import { useGetClosingStockForDaysV2 } from "~/utils/closingStockUtils"
import { filterIncomingOrdersInPeriod } from "~/utils/incomingOrdersUtils"
import {
  convertToStartOfWeekUTC,
  differenceInWeeksFloat,
  formatDate,
  getStartOfCurrentWeekUTC,
  startOfTodayUTC,
} from "~/utils/datetime"
import { getForecastBetweenWeeks } from "~/utils/forecastUtils"
import {
  applyPreorderFloorPerWarehouse,
  type SuggestedOrderCell,
} from "~/utils/fashionPreorderFloor"
import { filterVariants, SKU_LEVEL_FILTER_FIELDS } from "~/utils/filter"
import { useFilterFieldTypeResolver } from "~/hooks/useFilterFieldTypeResolver"
import { type AgGridReact } from "ag-grid-react"
import { type IRowNode } from "ag-grid-community"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { ProductTableColumn } from "~/utils/productTableUtils"
import { ActionButton } from "~/components/reusable/ActionButton"
import { useSessionStorage } from "usehooks-ts"
import {
  type ICollectionSimple,
  useGetCollections,
  useCollectionGroups,
} from "~/hooks/useCollections"
import CollectionsSelect from "~/components/collections/CollectionsSelect"
import { type ComparisonGroup } from "~/hooks/usePerCollectionComparisonData"
import { useStockOutTimesDetails } from "~/hooks/useStockOutTimesV2"
import { getStockOutTimesV2 } from "~/utils/calculateStockoutTimes"
import { useCompany } from "~/hooks/useCompany"
import { useIsLesDeux } from "~/hooks/useIsLesDeux"
import { fashionSuggestedOrderUnits } from "~/utils/fashionSuggestedOrder"
import { projectedStockStartWithoutForecast } from "~/utils/fashionSupplyMath"
import {
  DEFAULT_DEMAND_SPLIT_METHOD,
  demandCellKey,
  splitVariantDemand,
  type DemandCell,
  type DemandSplitMethod,
} from "~/utils/fashionSkuDemand"
import {
  parseFashionBuySettings,
  resolveOnlyPreOrdersByWarehouse,
  resolveScopedWarehouseIds,
} from "~/utils/fashionBuySettings"
import {
  type SkuSuggestedOrdersMap,
  type SuggestedOrderExplanation,
} from "~/components/purchase-plan/SupplyPurchaseOrderContent"
import { NO_VENDOR, resolveVariantVendorIdForPlan } from "~/utils/vendorUtils"

const EMPTY_SKU_IDS: ReadonlySet<string> = new Set()

export const Route = createFileRoute("/_auth/procurement/supply-plan/$purchasePlanId/_layout")({
  component: RouteComponent,
})

type ComparisonSelection = {
  type: "group" | "individual"
  id: number
  name: string
  collections: ICollectionSimple[]
}

enum PurchasePlanTabs {
  quantities = "Quantity",
  capacity = "Vendor Capacity",
  greige = "Greige",
}

// Maps path segments to tabs for easy lookup
const pathToTabMap: Record<string, PurchasePlanTabs> = {
  quantities: PurchasePlanTabs.quantities,
  capacity: PurchasePlanTabs.capacity,
  greige: PurchasePlanTabs.greige,
}

function getActiveTabFromPath(pathname: string, showQuantitiesTab?: boolean): PurchasePlanTabs {
  const segments = pathname.split("/")
  const lastSegment = segments[segments.length - 1]

  return (
    pathToTabMap[lastSegment ?? ""] ??
    (showQuantitiesTab ? PurchasePlanTabs.quantities : PurchasePlanTabs.capacity)
  )
}

function groupOrdersBySkuAndWarehouse(orders: ISuggestedOrder[]) {
  const grouped: Record<string, ISuggestedOrder[]> = {}

  orders.forEach((order) => {
    const key = `${order.skuId}-${order.warehouseId}`

    if (!grouped[key]) {
      grouped[key] = []
    }

    grouped[key].push(order)
  })

  return grouped
}

function distributeQuantity(
  totalQty: number,
  entries: { key: string; weight: number }[],
): Map<string, number> {
  const result = new Map<string, number>()
  if (entries.length === 0 || totalQty <= 0) return result

  const totalWeight = sum(entries.map((e) => e.weight))

  if (totalWeight === 0) {
    const base = Math.floor(totalQty / entries.length)
    const remainder = totalQty % entries.length
    entries.forEach((entry, i) => {
      result.set(entry.key, base + (i < remainder ? 1 : 0))
    })
    return result
  }

  let remaining = totalQty
  entries.forEach((entry, i) => {
    const isLast = i === entries.length - 1
    const qty = isLast ? remaining : Math.round((entry.weight / totalWeight) * totalQty)
    result.set(entry.key, qty)
    remaining -= qty
  })

  return result
}

function distributeAcrossWarehousesAndSkus(
  totalQty: number,
  skuIds: string[],
  variantId: string,
  variantWarehouseWeights: Map<string, Map<string, number>>,
  skuSizeSplitWeights: Map<string, number>,
  fallbackWarehouseIds: string[],
  orderDate: Date,
): ISuggestedOrder[] {
  if (totalQty <= 0 || skuIds.length === 0) return []

  const result: ISuggestedOrder[] = []
  const whWeights = variantWarehouseWeights.get(variantId)
  const warehouseEntries = whWeights
    ? Array.from(whWeights.entries()).map(([whId, weight]) => ({ key: whId, weight }))
    : fallbackWarehouseIds.map((whId) => ({ key: whId, weight: 1 }))

  const whDistribution = distributeQuantity(totalQty, warehouseEntries)

  for (const [whId, whQty] of whDistribution) {
    if (whQty <= 0) continue

    const skuEntries = skuIds.map((skuId) => ({
      key: skuId,
      weight: skuSizeSplitWeights.get(skuId) ?? 0,
    }))
    const skuDistribution = distributeQuantity(whQty, skuEntries)

    for (const [skuId, skuQty] of skuDistribution) {
      if (skuQty > 0) {
        result.push({ skuId, warehouseId: whId, quantity: skuQty, orderDate })
      }
    }
  }

  return result
}

// `sentQty + erpQty` is exactly what getPurchasedQuantities used to return as `purchasedQty`: sent
// purchase orders plus matched ERP incoming stock. Draft is deliberately NOT here — it is shown
// beside the figure, never inside it, so no planning calculation moves. Three maps below read the
// committed figure and must stay in lockstep, which is why this is spelled once.
const committedOf = (pq: { sentQty: number; erpQty: number }) => pq.sentQty + pq.erpQty

function isFashionCollections(
  collections: { collectionType: string | null; startDate: Date | null; endDate: Date | null }[],
) {
  const start = minBy(collections, (c) => c.startDate)?.startDate
  const end = maxBy(collections, (c) => c.endDate)?.endDate
  return start != null && end != null && collections.every((c) => c.collectionType === "mixed")
}

/** Shared empty lookup for variants with no warehouse weights — avoids a Map per variant. */
const EMPTY_WEIGHTS: ReadonlyMap<string, number> = new Map()

function RouteComponent() {
  return (
    // Shared filter scope so a filter applied on the Quantities tab persists to the
    // Capacity tab (and vice-versa). Keyed by the layout route so both sibling tabs
    // resolve the same scope instead of one per leaf route.
    <FilterScopeProvider scopeKey={Route.id}>
      <PurchasePlanLayoutContent />
    </FilterScopeProvider>
  )
}

type CollectionStarts = Pick<
  NonNullable<ReturnType<typeof useGetCollections>["data"]>[number],
  "startDate" | "rawStartDate"
>

/** When the buy lands: the earliest collection start, raw dates first. */
function earliestCollectionStart(collections: CollectionStarts[]): Date | undefined {
  const earliest = (dates: (Date | null | undefined)[]) =>
    dates.reduce<Date | undefined>((min, d) => (d && (!min || d < min) ? d : min), undefined)
  return (
    earliest(collections.map((c) => c.rawStartDate)) ??
    earliest(collections.map((c) => c.startDate))
  )
}

/**
 * skuId -> warehouseId -> recorded stock at a period bound. A pair with no reading is absent,
 * which consumers read as 0: the SKU had not been stocked yet.
 */
function toStockMap(rows: { skuId: string; warehouseId: string; closingStock: number }[]) {
  const map = new Map<string, Map<string, number>>()
  for (const row of rows) {
    let inner = map.get(row.skuId)
    if (!inner) {
      inner = new Map<string, number>()
      map.set(row.skuId, inner)
    }
    inner.set(row.warehouseId, row.closingStock ?? 0)
  }
  return map
}

function PurchasePlanLayoutContent() {
  const warehouseId = undefined
  const warehouses = usePrimaryWarehouses()
  const [isDownloadingCapacity, setIsDownloadingCapacity] = useState(false)
  const [isDownloadingGreige, setIsDownloadingGreige] = useState(false)
  const [showCost, setShowCost] = useState(false)
  const { pathname } = useLocation()
  const greigeGridRef = useRef<AgGridReact>(null)
  const capacityGridRef = useRef<AgGridReact>(null)
  const saveSnapshot = useSavePurchasePlanSnapshot()
  const { purchasePlanId } = Route.useParams()

  const { data: purchasePlan, isLoading: isLoadingPurchasePlan } = api.purchasePlan.get.useQuery({
    id: Number(purchasePlanId),
  })

  // PRO-1126: a buy scoped to a subset of warehouses must derive its suggested order from THOSE
  // warehouses only. Everything below iterates `stockoutTimes[…].details`, which carries every
  // warehouse the stockout calc knows about — summing it unscoped made a US-only buy default its
  // planned buy to the DK + US demand, and `deriveSkuWarehouseSplit`'s per-warehouse cap cannot
  // claw that back once the only in-scope warehouse has a suggested order of 0 on its own.
  // `null` = nothing to filter: the buy covers every primary warehouse, or predates the setting.
  const buyScopeWarehouseIds = useMemo(() => {
    const settings = parseFashionBuySettings(purchasePlan?.buySettings)
    if (!settings?.scopedWarehouseIds.length) return null
    const primaryIds = warehouses.map((warehouse) => warehouse.id)
    const scoped = resolveScopedWarehouseIds(settings, primaryIds)
    return scoped.length < primaryIds.length ? new Set(scoped) : null
  }, [purchasePlan?.buySettings, warehouses])

  const isInBuyScope = useCallback(
    (warehouseId: string) => buyScopeWarehouseIds?.has(warehouseId) ?? true,
    [buyScopeWarehouseIds],
  )

  /**
   * The buy's "no gambling" posture per warehouse. A pre-orders-only warehouse buys the
   * commitments it still owes and nothing else — `fashionWarehouseBaselineBuy` has always
   * resolved its planned buy that way, and the suggested order has to agree or the two columns
   * propose different totals (HAK-2609).
   */
  const onlyPreOrdersByWarehouse = useMemo(
    () =>
      resolveOnlyPreOrdersByWarehouse(
        parseFashionBuySettings(purchasePlan?.buySettings),
        warehouses,
      ),
    [purchasePlan?.buySettings, warehouses],
  )

  const collectionsEnabled = !!purchasePlan && purchasePlan.collectionIds.length > 0
  const collectionIds = purchasePlan?.collectionIds?.map((id) => Number(id))
  const { data: collections, isLoading: isLoadingCollections } = useGetCollections({
    collectionIds,
    allowNoCollections: !collectionsEnabled,
  })
  // Part of `dataIsReady`: a buy computed before this arrives would suggest and order OUT sizes.
  const { data: outSkuIdList, isLoading: outSkuIdsLoading } =
    api.purchasePlan.getOutSkuIds.useQuery(
      { collectionIds: purchasePlan?.collectionIds ?? [] },
      { enabled: collectionsEnabled },
    )
  // Get variant quantities from supply plan (saved in DB)
  const { data: variantQuantities, isLoading: variantQuantitiesLoading } =
    api.purchasePlan.getVariantQuantities.useQuery(
      { purchasePlanId: purchasePlan?.id ?? 0 },
      { enabled: !!purchasePlan },
    )

  const variantQuantityMap = useMemo(
    () => new Map(variantQuantities?.map((vq) => [vq.variantId, vq.nextPurchaseQty]) ?? []),
    [variantQuantities],
  )

  // Get already purchased quantities
  const { data: purchasedQuantities, isLoading: poQuantitiesLoading } =
    api.purchasePlan.getPurchasedQuantities.useQuery(
      { purchasePlanId: purchasePlan?.id ?? 0 },
      { enabled: !!purchasePlan },
    )

  const purchasedQuantityMap = useMemo(() => {
    const map = new Map<string, Map<string, number>>()
    for (const pq of purchasedQuantities ?? []) {
      if (!map.has(pq.variantId)) map.set(pq.variantId, new Map())
      const inner = map.get(pq.variantId)!
      inner.set(pq.warehouseId, (inner.get(pq.warehouseId) ?? 0) + committedOf(pq))
    }
    return map
  }, [purchasedQuantities])
  // Per-SKU total: sum across warehouses since getPurchasedQuantities returns one row per warehouse.
  const purchasedQuantityBySkuMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const pq of purchasedQuantities ?? []) {
      map.set(pq.skuId, (map.get(pq.skuId) ?? 0) + committedOf(pq))
    }
    return map
  }, [purchasedQuantities])
  // Per-SKU-per-warehouse — feeds FashionBuyTable's warehouse-aware "Already Purchased" column.
  const purchasedQuantityBySkuWarehouseMap = useMemo(() => {
    const map = new Map<string, Map<string, number>>()
    for (const pq of purchasedQuantities ?? []) {
      if (!map.has(pq.skuId)) map.set(pq.skuId, new Map())
      const inner = map.get(pq.skuId)!
      inner.set(pq.warehouseId, (inner.get(pq.warehouseId) ?? 0) + committedOf(pq))
    }
    return map
  }, [purchasedQuantities])
  // Shown beside the committed figure, never added into it.
  const draftQuantityBySkuWarehouseMap = useMemo(() => {
    const map = new Map<string, Map<string, number>>()
    for (const pq of purchasedQuantities ?? []) {
      if (!map.has(pq.skuId)) map.set(pq.skuId, new Map())
      const inner = map.get(pq.skuId)!
      inner.set(pq.warehouseId, (inner.get(pq.warehouseId) ?? 0) + pq.draftQty)
    }
    return map
  }, [purchasedQuantities])
  // Stated by the server instead of inferred by subtracting the PO lines from the total.
  const erpQuantityBySkuWarehouseMap = useMemo(() => {
    const map = new Map<string, Map<string, number>>()
    for (const pq of purchasedQuantities ?? []) {
      if (!map.has(pq.skuId)) map.set(pq.skuId, new Map())
      const inner = map.get(pq.skuId)!
      inner.set(pq.warehouseId, (inner.get(pq.warehouseId) ?? 0) + pq.erpQty)
    }
    return map
  }, [purchasedQuantities])

  // HAK-2288: the purchase orders behind the committed figures, so the "Already Purchased" cell
  // can link to them. Scoped identically to getPurchasedQuantities' PO half, so the hover
  // breakdown reconciles with the cell; sent and draft lines arrive together and the cell
  // partitions them by `status`. ERP-ingested incoming orders are not here — no PO to link.
  const { data: purchaseOrderLines } =
    api.purchasePlanPurchaseOrder.getLinesBySkuWarehouse.useQuery(
      { purchasePlanId: purchasePlan?.id ?? 0 },
      { enabled: !!purchasePlan },
    )
  const purchaseOrdersBySkuWarehouseMap = useMemo(() => {
    const map = new Map<string, Map<string, FashionWarehousePurchaseOrder[]>>()
    for (const line of purchaseOrderLines ?? []) {
      let byWarehouse = map.get(line.skuId)
      if (!byWarehouse) {
        byWarehouse = new Map()
        map.set(line.skuId, byWarehouse)
      }
      const lines = byWarehouse.get(line.warehouseId)
      const entry = {
        purchaseOrderId: line.purchaseOrderId,
        purchaseOrderName: line.purchaseOrderName,
        quantity: line.quantity,
        status: line.status,
        expectedArrivalDate: line.expectedArrivalDate,
      }
      if (lines) lines.push(entry)
      else byWarehouse.set(line.warehouseId, [entry])
    }
    return map
  }, [purchaseOrderLines])

  const { data: allGroups } = useCollectionGroups()

  const [comparisonSelections, setComparisonSelections] = useSessionStorage<ComparisonSelection[]>(
    `purchasePlan-comparisonSelections-${purchasePlanId}`,
    [],
  )

  const comparisonCollections = useMemo(
    () => comparisonSelections.flatMap((s) => s.collections),
    [comparisonSelections],
  )

  const comparisonCollectionIds = useMemo(
    () => comparisonCollections.map((c) => c.id),
    [comparisonCollections],
  )

  const selectedGroupIds = useMemo(
    () => comparisonSelections.filter((s) => s.type === "group").map((s) => s.id),
    [comparisonSelections],
  )

  const excludedSet = useMemo(() => new Set(collectionIds ?? []), [collectionIds])

  const comparisonGroups: ComparisonGroup[] = useMemo(() => {
    const seen = new Set<string>()
    const flattened: ComparisonGroup[] = []

    // Preserve selector order: selections order, then collections order within each selection.
    for (const selection of comparisonSelections) {
      if (selection.type === "group") {
        const key = `group:${selection.id}`
        if (seen.has(key)) continue
        seen.add(key)

        flattened.push({
          id: selection.id,
          name: selection.name,
          collectionIds: selection.collections.map((c) => c.id),
          externalCollectionIds: selection.collections
            .map((c) => c.externalId)
            .filter((id): id is string => typeof id === "string"),
        })
        continue
      }

      for (const c of selection.collections) {
        // Never compare plan collections against themselves.
        if (excludedSet.has(c.id)) continue
        const key = `collection:${c.id}`
        if (seen.has(key)) continue
        seen.add(key)

        flattened.push({
          id: c.id,
          name: c.name,
          collectionIds: [c.id],
          externalCollectionIds:
            typeof c.externalId === "string" && c.externalId.length > 0 ? [c.externalId] : [],
        })
      }
    }

    return flattened
  }, [comparisonSelections, excludedSet])

  const handleComparisonCollectionsChange = useCallback(
    (next: ICollectionSimple[]) => {
      if (next.length === 0) {
        setComparisonSelections([])
        return
      }

      const nextIds = new Set(next.map((c) => c.id))
      const newSelections: ComparisonSelection[] = []
      const processedIds = new Set<number>()

      for (const group of allGroups ?? []) {
        if (group.collectionIds.length <= 1) continue
        if (group.collectionIds.every((id) => nextIds.has(id))) {
          const groupCollections = next.filter((c) => c.collectionGroupId === group.id)
          newSelections.push({
            type: "group",
            id: group.id,
            name: group.name,
            collections: groupCollections,
          })
          groupCollections.forEach((c) => processedIds.add(c.id))
        }
      }

      for (const c of next) {
        if (!processedIds.has(c.id)) {
          newSelections.push({ type: "individual", id: c.id, name: c.name, collections: [c] })
        }
      }

      setComparisonSelections(newSelections)
    },
    [allGroups, setComparisonSelections],
  )

  const isLoading = isLoadingPurchasePlan || (collectionsEnabled && isLoadingCollections)
  const dataIsReady =
    !!purchasePlan &&
    (!collectionsEnabled || !isLoadingCollections) &&
    (!collectionsEnabled || !outSkuIdsLoading) &&
    !poQuantitiesLoading &&
    !variantQuantitiesLoading

  const rawStart = dataIsReady && collections ? earliestCollectionStart(collections) : undefined
  const end =
    dataIsReady && collections
      ? (maxBy(collections, (c) => c.endDate)?.endDate ?? undefined)
      : undefined
  const rawEnd =
    dataIsReady && collections
      ? (maxBy(collections, (c) => c.rawEndDate)?.rawEndDate ?? end ?? undefined)
      : undefined
  const isFashion = dataIsReady && collections ? isFashionCollections(collections) : false
  const isNOOSCollection =
    dataIsReady && collections
      ? (collections.every((c) => c.collectionType === "noos") ?? false)
      : false
  const { data: company } = useCompany()
  const isLesDeux = useIsLesDeux()
  // Per-call-site fashion gating: drop the lost-sales term only in a fashion context when the
  // company has opted out of including it. NOOS contexts always resolve to false (include).
  const ignoreLostSales = isFashion
    ? !(company?.includeLostSalesForFashionOrderSuggestions ?? false)
    : false
  const rawVariants = useVariantsByIds(purchasePlan?.variantIds ?? [])
  const variantIdsForStockout = useMemo(
    () => rawVariants?.map((v) => v.variantId) ?? [],
    [rawVariants],
  )

  // The raw per-(sku, warehouse) rows behind the client-side stockout calc and the suggested-order
  // memos below. The buy's arrival is part of the query, and it is known once the collections are,
  // so this runs alongside the plan's other loads rather than after them.
  const collectionsLoaded = !!purchasePlan && (!collectionsEnabled || !isLoadingCollections)
  const buyArrival = useMemo(() => {
    const first =
      collectionsLoaded && collections ? earliestCollectionStart(collections) : undefined
    return first ? convertToStartOfWeekUTC(first) : undefined
  }, [collectionsLoaded, collections])
  const { data: rawStockOutDetails } = useStockOutTimesDetails(
    variantIdsForStockout,
    buyArrival,
    collectionsLoaded,
  )
  const skuIds = useMemo(() => rawVariants?.flatMap((v) => v.skuIds) ?? [], [rawVariants])

  // Fashion only. Applied at the source (suggestion, pre-order floor, order list) so every tab
  // counts the same sizes.
  const outSkuIds = useMemo<ReadonlySet<string>>(
    () => (isFashion && outSkuIdList?.length ? new Set(outSkuIdList) : EMPTY_SKU_IDS),
    [isFashion, outSkuIdList],
  )

  /**
   * A buy has ONE arrival date — the period start — so whether its supply figures are projections
   * or history is a property of the plan, not of each variant. Once that date has passed the
   * stockout projection has nothing left to project: `calculateStockLevelAtWeek` with a negative
   * arrival week returns today's stock, which the "start of period" column then reports as the
   * opening position. Past the start, recorded readings replace it for every SKU (PRO-1364).
   */
  const periodStartHasPassed = rawStart != null && isBefore(rawStart, startOfTodayUTC())
  /** Past the END too, so nothing more can land and the closing position is equally a fact. */
  const periodEndHasPassed = rawEnd != null && isBefore(rawEnd, startOfTodayUTC())

  const warehouseIdsForActuals = useMemo(() => warehouses.map((w) => w.id), [warehouses])

  // Only fetched once the period has started — a forward-looking buy keeps the projection and
  // pays nothing.
  const { data: periodSupplyActuals } = api.sku.getPeriodSupplyActuals.useQuery(
    {
      skuIds,
      warehouseIds: warehouseIdsForActuals,
      periodStart: rawStart!,
      periodEnd: rawEnd!,
    },
    {
      enabled:
        isFashion &&
        periodStartHasPassed &&
        skuIds.length > 0 &&
        warehouseIdsForActuals.length > 0 &&
        rawStart != null &&
        rawEnd != null,
    },
  )

  const actualStockAtStartBySkuWarehouse = useMemo(
    () => toStockMap(periodSupplyActuals?.stockAtStart ?? []),
    [periodSupplyActuals],
  )

  const actualStockAtEndBySkuWarehouse = useMemo(
    () => toStockMap(periodSupplyActuals?.stockAtEnd ?? []),
    [periodSupplyActuals],
  )

  // skuId -> warehouseId -> orders that arrived inside the period. Shaped like the stockout calc's
  // incoming orders so the row builder can append them and the existing date filters just work.
  const arrivedOrdersBySkuWarehouse = useMemo(() => {
    const map = new Map<string, Map<string, { quantity: number; expectedArrivalDate: Date }[]>>()
    for (const row of periodSupplyActuals?.arrivedInPeriod ?? []) {
      let inner = map.get(row.skuId)
      if (!inner) {
        inner = new Map<string, { quantity: number; expectedArrivalDate: Date }[]>()
        map.set(row.skuId, inner)
      }
      const orders = inner.get(row.warehouseId) ?? []
      orders.push({
        quantity: row.quantity,
        expectedArrivalDate: new Date(row.expectedArrivalDate),
      })
      inner.set(row.warehouseId, orders)
    }
    return map
  }, [periodSupplyActuals])

  // Align the suggested-order window to whole ISO weeks so the cumulative-forecast
  // interpolation reads at integer week boundaries. Without this, fractional weeks
  // at the period end shave units off suggested order vs the Forecast column's
  // bucket-sum, producing an apparent negative projected stock end of period.
  const { stockoutWindowStart, stockoutWindowEnd } = useMemo(
    () => ({
      stockoutWindowStart: rawStart ? convertToStartOfWeekUTC(rawStart) : undefined,
      stockoutWindowEnd: rawEnd ? addDays(convertToStartOfWeekUTC(rawEnd), 7) : undefined,
    }),
    [rawStart, rawEnd],
  )
  const stockoutTimes = useMemo(() => {
    if (!company || !rawStockOutDetails) return undefined
    return getStockOutTimesV2(
      company.overstockThresholdDays,
      rawStockOutDetails,
      stockoutWindowStart,
      stockoutWindowEnd,
      undefined,
      undefined,
      false,
      false,
      undefined,
      ignoreLostSales,
    )
  }, [company, rawStockOutDetails, stockoutWindowStart, stockoutWindowEnd, ignoreLostSales])

  // skuId -> warehouseId -> stock at the start of the period: recorded once it has started, else the
  // stockout calc's projection, else (no forecast row, SUP-477) today's stock plus arrivals up to it.
  // The one source for the Projected Stock column, the pre-order floor and the variant total.
  const { data: stockLevelsByVariant } = useStockLevels(isFashion && !periodStartHasPassed)
  const projectedStockStartBySkuWarehouseMap = useMemo<Map<string, Map<string, number>>>(() => {
    const map = new Map<string, Map<string, number>>()
    if (stockoutTimes == undefined) return map

    if (periodStartHasPassed) {
      for (const variant of rawVariants) {
        for (const skuId of variant.skuIds) {
          const inner = new Map<string, number>()
          for (const warehouseId of warehouseIdsForActuals) {
            inner.set(
              warehouseId,
              actualStockAtStartBySkuWarehouse.get(skuId)?.get(warehouseId) ?? 0,
            )
          }
          map.set(skuId, inner)
        }
      }
      return map
    }

    for (const variant of rawVariants) {
      const variantStockLevels = stockLevelsByVariant?.get(variant.variantId) ?? []
      let withoutForecast: Map<string, Map<string, number>> | undefined
      for (const skuId of variant.skuIds) {
        const details = stockoutTimes.get(skuId)?.details
        if (details) {
          const inner = new Map<string, number>()
          for (const [warehouseId, detail] of Object.entries(details)) {
            inner.set(warehouseId, detail.stockLevelAtArrival)
          }
          if (inner.size > 0) map.set(skuId, inner)
          continue
        }
        if (!isFashion || !stockoutWindowStart) continue
        withoutForecast ??= projectedStockStartWithoutForecast(
          variantStockLevels,
          variant.incomingOrdersWarehouse ?? [],
          stockoutWindowStart,
        )
        const byWarehouse = withoutForecast.get(skuId)
        if (byWarehouse) map.set(skuId, byWarehouse)
      }
    }
    return map
  }, [
    rawVariants,
    stockoutTimes,
    periodStartHasPassed,
    actualStockAtStartBySkuWarehouse,
    warehouseIdsForActuals,
    isFashion,
    stockoutWindowStart,
    stockLevelsByVariant,
  ])

  // Week offsets from the current week to the suggested-order window bounds — the same
  // `arrivalWeek` / `nextArrivalWeek` `calculateSuggestedOrder` works in. Defined once so the
  // per-cell inputs below and `alignedVariantForecasts` cannot drift apart.
  const { arrivalWeek, nextArrivalWeek } = useMemo(() => {
    if (!stockoutWindowStart || !stockoutWindowEnd) {
      return { arrivalWeek: undefined, nextArrivalWeek: undefined }
    }
    const day = getStartOfCurrentWeekUTC()
    return {
      arrivalWeek: differenceInWeeksFloat(day, stockoutWindowStart),
      nextArrivalWeek: differenceInWeeksFloat(day, stockoutWindowEnd),
    }
  }, [stockoutWindowStart, stockoutWindowEnd])

  // Per-(sku, warehouse) inputs behind a cell's suggested order, for the buy table's explanation
  // tooltip. Mirrors `calculateSuggestedOrder`: demand is the in-window forecast, and in-period
  // incoming sums `weekDiff > arrivalWeek && weekDiff <= nextArrivalWeek` over incoming orders
  // merged with forecasted returns. One pass over the rows, keyed for O(1) lookup below.
  const suggestedOrderInputsByCell = useMemo(() => {
    const map = new Map<string, { demand: number; incomingInPeriod: number }>()
    if (!rawStockOutDetails || arrivalWeek === undefined || nextArrivalWeek === undefined) {
      return map
    }
    for (const entry of rawStockOutDetails) {
      let incomingInPeriod = 0
      for (const order of [...(entry.incomingOrders ?? []), ...(entry.forecastedReturns ?? [])]) {
        if (order.weekDiff > arrivalWeek && order.weekDiff <= nextArrivalWeek) {
          incomingInPeriod += order.quantity
        }
      }
      map.set(`${entry.skuId}///${entry.warehouseId}`, {
        demand: getForecastBetweenWeeks(entry.forecasts, arrivalWeek, nextArrivalWeek),
        incomingInPeriod,
      })
    }
    return map
  }, [rawStockOutDetails, arrivalWeek, nextArrivalWeek])

  // Unfiltered (all-warehouse) company-wide total — demandPerWarehouse/suggestedOrder split this
  // by each SKU's pre-order share, so the warehouse selector narrows what is shown rather than
  // re-dividing the variant's demand; must NOT be pre-scoped to the selected warehouse here.
  const { data: variantForecasts } = api.forecasts.getVariantForecastForCollections.useQuery(
    { collectionIds: collectionIds ?? [] },
    // Not on `dataIsReady`: only the collections decide fashion.
    {
      enabled:
        !!collectionIds &&
        (!collectionsEnabled || !!collections) &&
        !isFashionCollections(collections ?? []),
    },
  )

  // Whole published season, no live fallback. All-warehouse as above: do NOT pass the selected warehouse.
  const { data: publishedVariantDemand } = api.forecasts.getPublishedDemandForCollections.useQuery(
    { collectionIds: collectionIds ?? [] },
    { enabled: isFashion && (collectionIds?.length ?? 0) > 0 },
  )

  const { data: preordersBySkuWarehouse } =
    api.preOrder.getPreordersForCollectionsBySkuWarehouse.useQuery(
      { collectionIds: collectionIds ?? [] },
      { enabled: isFashion && (collectionIds?.length ?? 0) > 0 },
    )

  // Pre-orders net of what has already been bought into the warehouse. React Query dedupes this
  // with the identical read in `useFashionSkuRows`, which the Planned Buy baseline uses — sharing
  // the figure is the point, so the two columns cannot disagree.
  const { data: remainingPreordersRaw } =
    api.preOrder.getRemainingPreordersForCollectionsBySkuWarehouse.useQuery(
      { collectionIds: collectionIds ?? [] },
      { enabled: isFashion && (collectionIds?.length ?? 0) > 0 },
    )
  const remainingPreordersBySkuWarehouse = useMemo(() => {
    const map = new Map<string, Map<string, number>>()
    for (const bySku of remainingPreordersRaw?.values() ?? []) {
      for (const [skuId, byWarehouse] of bySku) {
        let inner = map.get(skuId)
        if (!inner) {
          inner = new Map<string, number>()
          map.set(skuId, inner)
        }
        for (const [warehouseId, remaining] of byWarehouse) {
          inner.set(warehouseId, (inner.get(warehouseId) ?? 0) + remaining)
        }
      }
    }
    return map
  }, [remainingPreordersRaw])

  // Size split weights for distributing quantities across SKUs
  const { data: sizeSplitWeights } = api.sku.getSizeSplitForSkus.useQuery(
    { skuIds },
    { enabled: skuIds.length > 0 && isFashion },
  )

  // Warehouse weights for distributing quantities across warehouses
  const { data: warehouseWeights } = api.sku.getWarehouseWeightsForVariants.useQuery(
    { variantIds: rawVariants.map((v) => v.variantId) },
    { enabled: rawVariants.length > 0 && isFashion },
  )

  const sizeSplitWeightsMap = useMemo(() => {
    if (!sizeSplitWeights) return new Map<string, number>()
    return new Map(sizeSplitWeights.map((w) => [w.skuId, w.weight]))
  }, [sizeSplitWeights])

  const warehouseWeightsMap = useMemo(() => {
    const map = new Map<string, Map<string, number>>()
    if (!warehouseWeights) return map
    for (const row of warehouseWeights) {
      if (!map.has(row.variantId)) {
        map.set(row.variantId, new Map())
      }
      map.get(row.variantId)!.set(row.warehouseId, row.weight)
    }
    return map
  }, [warehouseWeights])

  // Reads the per-(sku, warehouse) forecast array straight from getStockOutTimesDetails — the
  // same source calculateSuggestedOrder's `demand` uses — so this column tracks the same data
  // SO does, avoiding the precision drift between useForecasts (raw forecast × 4-decimal weight)
  // and SO (3-decimal-rounded forecast × full-precision weight).
  //
  // Sums the raw per-cell demands; the display formatter rounds for presentation. Avoids
  // ceiling per cell so weighted per-SKU fractional remainders don't accumulate upward and
  // push the variant total above what the user published in the Demand Plan (e.g. a 1500-unit
  // published forecast drifting to 1503 with three SKUs).
  //
  // Falls back to the variant-level server value when the per-cell sum is 0.
  const alignedVariantForecasts = useMemo<Map<string, number>>(() => {
    const result = new Map<string, number>()
    const detailsByVariant = groupBy(rawStockOutDetails ?? [], "variantId")

    for (const variant of rawVariants) {
      // Fashion demand is what was published; unpublished reads 0, never the live sum.
      if (isFashion) {
        if (publishedVariantDemand) {
          result.set(variant.variantId, publishedVariantDemand.get(variant.variantId) ?? 0)
        }
        continue
      }
      // Everything below needs the buy's arrival window to sum a forecast over.
      if (arrivalWeek === undefined || nextArrivalWeek === undefined || !rawStockOutDetails) {
        continue
      }

      let total = 0
      for (const entry of detailsByVariant[variant.variantId] ?? []) {
        total += getForecastBetweenWeeks(entry.forecasts, arrivalWeek, nextArrivalWeek)
      }

      const fallback = variantForecasts?.get(variant.variantId) ?? 0
      result.set(variant.variantId, total > 0 ? total : fallback)
    }

    return result
  }, [
    rawVariants,
    rawStockOutDetails,
    arrivalWeek,
    nextArrivalWeek,
    variantForecasts,
    publishedVariantDemand,
    isFashion,
  ])

  /**
   * Which weights divide a variant's demand across its sizes. `preorders` is the Les Deux switch
   * (`purchasePlan.skuSplitMethod`), double-gated on the org so a stored value can never move
   * another company's numbers — the same gate the toggle that writes it carries.
   */
  const demandSplitMethod: DemandSplitMethod =
    isLesDeux && purchasePlan?.skuSplitMethod === "preorders"
      ? "preorders"
      : DEFAULT_DEMAND_SPLIT_METHOD

  /**
   * skuId -> warehouseId -> demand: the variant's demand divided across its cells. THE source for
   * the "Demand" column, the suggested order below it, and (through that) the planned buy — so the
   * switch above only has to change this one map for the whole tab to follow.
   *
   * Shares are taken over every warehouse the variant forecasts to, not the buy's scope, so
   * narrowing the selector hides cells instead of re-dividing the variant's demand.
   */
  const skuDemandBySkuWarehouseMap = useMemo<Map<string, Map<string, number>>>(() => {
    const map = new Map<string, Map<string, number>>()
    if (!isFashion || !rawStockOutDetails) return map

    const detailsByVariant = groupBy(rawStockOutDetails, "variantId")
    for (const variant of rawVariants) {
      // A variant the stockout calc has no rows for still has published demand, and it still has
      // sizes to spread it over — skipping it here would drop that demand off the table entirely
      // and break the roll-up. Fall back to every (size, primary warehouse) pair and let the
      // split's own cascade pick the weights.
      const entries = detailsByVariant[variant.variantId] ?? []
      const cellsFromDetails = entries.map((entry) => ({
        skuId: entry.skuId,
        warehouseId: entry.warehouseId,
      }))
      const cells: DemandCell[] =
        cellsFromDetails.length > 0
          ? cellsFromDetails
          : variant.skuIds.flatMap((skuId) =>
              warehouses.map((warehouse) => ({ skuId, warehouseId: warehouse.id })),
            )
      if (cells.length === 0) continue

      const forecastByCell = new Map<string, number>()
      const preordersByCell = new Map<string, number>()
      for (const cell of cells) {
        const key = demandCellKey(cell.skuId, cell.warehouseId)
        forecastByCell.set(key, suggestedOrderInputsByCell.get(key)?.demand ?? 0)
        preordersByCell.set(
          key,
          preordersBySkuWarehouse?.get(cell.skuId)?.get(cell.warehouseId) ?? 0,
        )
      }

      const split = splitVariantDemand({
        variantDemand: alignedVariantForecasts.get(variant.variantId) ?? 0,
        cells,
        method: demandSplitMethod,
        forecastByCell,
        preordersByCell,
        sizeWeights: sizeSplitWeightsMap,
        warehouseWeights: warehouseWeightsMap.get(variant.variantId) ?? EMPTY_WEIGHTS,
      })

      for (const cell of cells) {
        let inner = map.get(cell.skuId)
        if (!inner) {
          inner = new Map<string, number>()
          map.set(cell.skuId, inner)
        }
        inner.set(cell.warehouseId, split.get(demandCellKey(cell.skuId, cell.warehouseId)) ?? 0)
      }
    }
    return map
  }, [
    isFashion,
    rawStockOutDetails,
    rawVariants,
    warehouses,
    alignedVariantForecasts,
    demandSplitMethod,
    suggestedOrderInputsByCell,
    preordersBySkuWarehouse,
    sizeSplitWeightsMap,
    warehouseWeightsMap,
  ])

  // Per-(sku, warehouse) suggested order, before the pre-order floor: the cell's own demand net of
  // the stock it will already have at arrival and what is inbound during the period. No
  // safety-stock buffer, clamped to 0 — see `fashionSuggestedOrderUnits`. `stockLevelAtArrival` and
  // the in-period incoming are still the stockout calc's; only the arithmetic on top is ours.
  const suggestedOrderByCell = useMemo(() => {
    const map = new Map<string, number>()
    if (!isFashion || !stockoutTimes) return map
    // Period under way: net the published demand against what was actually on hand when it opened
    // plus everything landing inside it. The two incoming terms are disjoint — `arrived` rows sit
    // before today, the stockout calc's `weekDiff` is clamped to today onwards — so together they
    // are the period's whole inbound, with no overlap (PRO-1364).
    if (periodStartHasPassed) {
      for (const variant of rawVariants) {
        for (const skuId of variant.skuIds) {
          if (outSkuIds.has(skuId)) continue
          for (const warehouseId of warehouseIdsForActuals) {
            const key = demandCellKey(skuId, warehouseId)
            const arrived = sumBy(
              arrivedOrdersBySkuWarehouse.get(skuId)?.get(warehouseId) ?? [],
              "quantity",
            )
            map.set(
              key,
              fashionSuggestedOrderUnits(
                skuDemandBySkuWarehouseMap.get(skuId)?.get(warehouseId) ?? 0,
                arrived + (suggestedOrderInputsByCell.get(key)?.incomingInPeriod ?? 0),
                actualStockAtStartBySkuWarehouse.get(skuId)?.get(warehouseId) ?? 0,
              ),
            )
          }
        }
      }
      return map
    }

    for (const variant of rawVariants) {
      for (const skuId of variant.skuIds) {
        if (outSkuIds.has(skuId)) continue
        const details = stockoutTimes.get(skuId)?.details
        if (!details) continue
        for (const [warehouseId, detail] of Object.entries(details)) {
          const key = demandCellKey(skuId, warehouseId)
          map.set(
            key,
            fashionSuggestedOrderUnits(
              skuDemandBySkuWarehouseMap.get(skuId)?.get(warehouseId) ?? 0,
              suggestedOrderInputsByCell.get(key)?.incomingInPeriod ?? 0,
              detail.stockLevelAtArrival,
            ),
          )
        }
      }
    }
    return map
  }, [
    isFashion,
    stockoutTimes,
    rawVariants,
    skuDemandBySkuWarehouseMap,
    suggestedOrderInputsByCell,
    periodStartHasPassed,
    warehouseIdsForActuals,
    arrivedOrdersBySkuWarehouse,
    actualStockAtStartBySkuWarehouse,
    outSkuIds,
  ])

  // Base per-cell suggested order before the pre-order floor. `fallback` is the caller's
  // `detail.suggestedOrder`, kept for non-fashion plans, which still read the stockout calc's
  // buffered figure. A fashion cell missing from the map has no demand, so nothing to buy.
  const getCellBaseSuggestedOrder = useCallback(
    (skuId: string, warehouseId: string, fallback: number): number => {
      if (!isFashion) return fallback
      return suggestedOrderByCell.get(demandCellKey(skuId, warehouseId)) ?? 0
    },
    [isFashion, suggestedOrderByCell],
  )

  const flooredCellsByVariant = useMemo(() => {
    const result = new Map<string, SuggestedOrderCell[]>()
    if (!isFashion || !stockoutTimes) return result
    const scopedWarehouseIds = warehouseIdsForActuals.filter(isInBuyScope)
    for (const variant of rawVariants) {
      // Kept per warehouse rather than summed: the floor tops each warehouse up against what that
      // warehouse owes, so pooling them here would be the bug it exists to avoid.
      const cells: SuggestedOrderCell[] = []
      const projectedStockByWarehouse = new Map<string, number>()
      const preordersByWarehouse = new Map<string, number>()
      for (const skuId of variant.skuIds) {
        // An OUT size is not bought, so neither its pre-orders nor its stock enter the floor.
        if (outSkuIds.has(skuId)) continue
        const details = stockoutTimes.get(skuId)?.details
        /**
         * A pre-orders-only warehouse owes its REMAINING commitments: pre-orders net of what has
         * already been bought into it, which is exactly what `fashionWarehouseBaselineBuy` buys
         * there. Flooring it against the ABSOLUTE pre-orders lifted it back to the full commitment
         * even once the goods were on the water, so the variant's suggested total exceeded what any
         * warehouse would actually buy — and the surplus landed on a risk-taking warehouse when the
         * buy was split across warehouses, overstocking it (HAK-2609).
         */
        const absolutePreorders = preordersBySkuWarehouse?.get(skuId)
        const remainingPreorders = remainingPreordersBySkuWarehouse.get(skuId)
        for (const warehouseId of new Set([
          ...(absolutePreorders?.keys() ?? []),
          ...(remainingPreorders?.keys() ?? []),
        ])) {
          if (!isInBuyScope(warehouseId)) continue
          const owed = onlyPreOrdersByWarehouse.get(warehouseId)
            ? (remainingPreorders?.get(warehouseId) ?? 0)
            : (absolutePreorders?.get(warehouseId) ?? 0)
          preordersByWarehouse.set(warehouseId, (preordersByWarehouse.get(warehouseId) ?? 0) + owed)
        }
        // A variant whose season has closed has no `ForecastVariantCompressed` rows, so the
        // stockout calc emits no detail for it — yet it still has published demand, stock and
        // arrivals. Skipping it here left the variant with no cells at all, and the empty (but
        // truthy) array then short-circuited the suggested-order map into 0 (HAK-2609). Fall back
        // to a cell per warehouse so the suggestion and the floor have somewhere to land.
        const warehouseCells: [
          string,
          { stockLevelAtArrival: number; suggestedOrder?: number } | undefined,
        ][] = details
          ? Object.entries(details).map(([warehouseId, detail]) => [warehouseId, detail])
          : scopedWarehouseIds.map((warehouseId) => [warehouseId, undefined])
        for (const [warehouseId, detail] of warehouseCells) {
          if (!isInBuyScope(warehouseId)) continue
          cells.push({
            skuId,
            warehouseId,
            suggestedOrder: getCellBaseSuggestedOrder(
              skuId,
              warehouseId,
              detail?.suggestedOrder ?? 0,
            ),
          })
          // Left at 0 for a pre-orders-only warehouse: `remaining` above already nets what has been
          // bought into it, so subtracting stock and arrivals again would net the same units twice.
          if (onlyPreOrdersByWarehouse.get(warehouseId)) continue
          projectedStockByWarehouse.set(
            warehouseId,
            (projectedStockByWarehouse.get(warehouseId) ?? 0) +
              (projectedStockStartBySkuWarehouseMap.get(skuId)?.get(warehouseId) ?? 0),
          )
        }
      }
      // `incomingOrdersWarehouse`, NOT `incomingOrders`: the latter is pre-filtered to the GLOBAL
      // warehouse selector by useVariantTableDataHeavy, and it is the only selector-scoped figure
      // that reaches the buy's arithmetic. Reading it made the pre-order floor — and therefore
      // every quantity derived from it — move as a planner switched the selector between US, DK and
      // all warehouses, with nothing about the buy having changed. Every other input here is
      // queried without a warehouse (stockout details, pre-orders per warehouse, size splits), so
      // the buy's own scope is the only warehouse filter that belongs on this number.
      const incomingByWarehouse = new Map<string, number>()
      if (stockoutWindowStart && stockoutWindowEnd) {
        for (const order of filterIncomingOrdersInPeriod(
          variant.incomingOrdersWarehouse ?? [],
          stockoutWindowStart,
          stockoutWindowEnd,
        )) {
          if (!isInBuyScope(order.warehouseId)) continue
          if (onlyPreOrdersByWarehouse.get(order.warehouseId)) continue
          incomingByWarehouse.set(
            order.warehouseId,
            (incomingByWarehouse.get(order.warehouseId) ?? 0) + order.quantity,
          )
        }
      }
      // `incomingOrdersWarehouse` carries status `incoming` only, so for a period whose deliveries
      // have landed it reports almost nothing and the floor re-orders goods already received. The
      // two sets are disjoint — arrived rows sit in the past, inbound ones from today onward.
      if (periodStartHasPassed) {
        for (const skuId of variant.skuIds) {
          if (outSkuIds.has(skuId)) continue
          const byWarehouse = arrivedOrdersBySkuWarehouse.get(skuId)
          if (!byWarehouse) continue
          for (const [warehouseId, orders] of byWarehouse) {
            if (!isInBuyScope(warehouseId) || onlyPreOrdersByWarehouse.get(warehouseId)) continue
            incomingByWarehouse.set(
              warehouseId,
              (incomingByWarehouse.get(warehouseId) ?? 0) + sumBy(orders, "quantity"),
            )
          }
        }
      }
      result.set(
        variant.variantId,
        applyPreorderFloorPerWarehouse({
          cells,
          preordersByWarehouse,
          projectedStockByWarehouse,
          incomingByWarehouse,
          sizeWeights: sizeSplitWeightsMap,
        }),
      )
    }
    return result
  }, [
    isFashion,
    stockoutTimes,
    rawVariants,
    stockoutWindowStart,
    stockoutWindowEnd,
    preordersBySkuWarehouse,
    sizeSplitWeightsMap,
    getCellBaseSuggestedOrder,
    isInBuyScope,
    periodStartHasPassed,
    arrivedOrdersBySkuWarehouse,
    warehouseIdsForActuals,
    onlyPreOrdersByWarehouse,
    remainingPreordersBySkuWarehouse,
    projectedStockStartBySkuWarehouseMap,
    outSkuIds,
  ])

  // Helper to get suggested order for a variant (total and per-SKU breakdown)
  const getSuggestedOrderForVariant = useCallback(
    (
      variantSkuIds: string[] | undefined,
    ): { total: number; skuOrders: Map<string, number> } | undefined => {
      if (!variantSkuIds?.length || !stockoutTimes) return undefined

      const skuOrders = new Map<string, number>()

      for (const skuId of variantSkuIds) {
        const stockoutTime = stockoutTimes?.get(skuId)
        const skuSuggestedOrder = stockoutTime?.details
          ? sum(
              Object.entries(stockoutTime.details)
                .filter(([warehouseId]) => isInBuyScope(warehouseId))
                .map(([warehouseId, detail]) =>
                  getCellBaseSuggestedOrder(skuId, warehouseId, detail.suggestedOrder ?? 0),
                ),
            )
          : 0
        if (skuSuggestedOrder > 0) {
          skuOrders.set(skuId, skuSuggestedOrder)
        }
      }

      const total = sum(Array.from(skuOrders.values()))

      return total > 0 ? { total, skuOrders } : undefined
    },
    [stockoutTimes, getCellBaseSuggestedOrder, isInBuyScope],
  )

  const showQuantitiesTab = isFashion
  const activeTab = getActiveTabFromPath(pathname, showQuantitiesTab)

  const navigate = useNavigate()

  useEffect(() => {
    if (
      dataIsReady &&
      !showQuantitiesTab &&
      activeTab === PurchasePlanTabs.quantities &&
      pathname.endsWith("/quantities")
    ) {
      navigate({
        to: "/procurement/supply-plan/$purchasePlanId/capacity",
        params: { purchasePlanId },
        replace: true,
      })
    }
  }, [dataIsReady, showQuantitiesTab, activeTab, pathname, navigate, purchasePlanId])

  const tabs: HeaderTab[] = useMemo(
    () => [
      ...(showQuantitiesTab
        ? [
            {
              label: PurchasePlanTabs.quantities,
              icon: <ListNumbers />,
              path: "/procurement/supply-plan/$purchasePlanId/quantities" as const,
            },
          ]
        : []),
      {
        label: PurchasePlanTabs.capacity,
        icon: <Factory />,
        path: "/procurement/supply-plan/$purchasePlanId/capacity" as const,
      },
      {
        label: PurchasePlanTabs.greige,
        icon: <Yarn />,
        path: "/procurement/supply-plan/$purchasePlanId/greige" as const,
      },
    ],
    [showQuantitiesTab],
  )

  // Memoized so their references stay stable across renders — downstream tab column
  // memos depend on these dates, and unstable Date refs caused ag-grid header re-wire loops.
  const calculationStartDate = useMemo(
    () =>
      dataIsReady && isFashion && rawStart != null && rawEnd != null
        ? rawStart
        : getStartOfCurrentWeekUTC(),
    [dataIsReady, isFashion, rawStart, rawEnd],
  )
  const calculationEndDate = useMemo(
    () =>
      dataIsReady && isFashion && rawStart != null && rawEnd != null
        ? rawEnd
        : addMonths(calculationStartDate, 15),
    [dataIsReady, isFashion, rawStart, rawEnd, calculationStartDate],
  )

  const variants: Array<SupplyPlanVariant> = rawVariants.map((variant) => {
    const variantSuggestedOrder = getSuggestedOrderForVariant(variant.skuIds)
    // `variant.suggestedOrder` is the variant store's all-warehouse figure, so a scoped buy has to
    // read 0 rather than borrow it when its own warehouses suggest nothing.
    // The store's figure counts OUT sizes' demand, so a variant with any OUT size never borrows it.
    const hasOutSize = variant.skuIds.some((id) => outSkuIds.has(id))
    let suggestedOrder =
      variantSuggestedOrder?.total ??
      (buyScopeWarehouseIds || hasOutSize ? 0 : variant.suggestedOrder)
    let skuSuggestedOrders = variantSuggestedOrder?.skuOrders ?? new Map<string, number>()

    const flooredCells = flooredCellsByVariant.get(variant.variantId)
    if (flooredCells) {
      const flooredTotal = flooredCells.reduce((acc, c) => acc + c.suggestedOrder, 0)
      if (flooredTotal > suggestedOrder) {
        const flooredPerSku = new Map<string, number>()
        for (const c of flooredCells) {
          flooredPerSku.set(c.skuId, (flooredPerSku.get(c.skuId) ?? 0) + c.suggestedOrder)
        }
        skuSuggestedOrders = flooredPerSku
        suggestedOrder = flooredTotal
      }
    }

    const purchasedQtyPerWarehouse =
      purchasedQuantityMap.get(variant.variantId) ?? new Map<string, number>()
    const purchasedQty = [...purchasedQtyPerWarehouse.values()].reduce((a, b) => a + b, 0)
    const savedQty = variantQuantityMap.get(variant.variantId)
    const quantity = isFashion ? (savedQty ?? null) : null

    let projectedStockStartOfPeriod = 0
    for (const skuId of variant.skuIds) {
      for (const stock of projectedStockStartBySkuWarehouseMap.get(skuId)?.values() ?? []) {
        projectedStockStartOfPeriod += stock
      }
    }
    // Same rule as the pre-order floor above: all warehouses from the store, narrowed by the buy's
    // scope, never by the global warehouse selector.
    const incomingInPeriodOrders =
      stockoutWindowStart && stockoutWindowEnd
        ? filterIncomingOrdersInPeriod(
            variant.incomingOrdersWarehouse ?? [],
            stockoutWindowStart,
            stockoutWindowEnd,
          ).filter((o) => isInBuyScope(o.warehouseId))
        : []
    const incomingInPeriod = incomingInPeriodOrders.reduce((acc, o) => acc + o.quantity, 0)

    return {
      ...variant,
      skuSuggestedOrders,
      purchasedQty,
      purchasedQtyPerWarehouse,
      suggestedOrder,
      quantity,
      projectedStockStartOfPeriod,
      incomingInPeriodOrders,
      incomingInPeriod,
    }
  })

  const { filters } = useFilters()
  const getFieldType = useFilterFieldTypeResolver()
  // SKU-level filters (size, size dimensions) are applied inside the Quantities tab; exclude them
  // here so they don't drop whole variants in the variant-level filter pass.
  const variantLevelFilters = useMemo(
    () => filters.filter((f) => !SKU_LEVEL_FILTER_FIELDS.has(f.field)),
    [filters],
  )
  const filteredVariants = filterVariants(
    variantLevelFilters,
    variants,
    undefined,
    getFieldType,
    collectionIds,
  ) as Array<SupplyPlanVariant>

  const closingStockData = useGetClosingStockForDaysV2({
    variantIds: dataIsReady ? filteredVariants.map((v) => v.variantId) : [],
    warehouseId,
    startDate: calculationStartDate,
    endDate: calculationEndDate,
    monthly: false,
    isContinuous: !collectionsEnabled || isNOOSCollection, // Should not be continuous if not a collection
    includeFashionOrders: true,
    // keep the closing-stock graph consistent with the suggested-order column
    ignoreLostSales,
  })

  const longestLeadTime = useMemo(() => {
    if (!dataIsReady || variants.length === 0) return 0
    const leadtime = variants.map((v) => v.settings?.totalLeadTimeDays ?? 0)
    return Math.max(0, ...leadtime)
  }, [variants, dataIsReady])

  const graphicalStartDate = useMemo(
    () =>
      dataIsReady && isFashion
        ? subMonths(subDays(rawStart ?? calculationStartDate, longestLeadTime), 1)
        : (rawStart ?? calculationStartDate),
    [dataIsReady, isFashion, rawStart, calculationStartDate, longestLeadTime],
  )
  const graphicalEndDate = useMemo(
    () =>
      dataIsReady && isFashion
        ? addMonths(rawEnd ?? calculationEndDate, 1)
        : (rawEnd ?? calculationEndDate),
    [dataIsReady, isFashion, rawEnd, calculationEndDate],
  )

  const dateColumns = useMemo(() => {
    if (!dataIsReady) return []
    const diff = differenceInCalendarMonths(graphicalEndDate, graphicalStartDate)
    const monthCount = Number.isFinite(diff) ? Math.max(0, diff) : 0
    return [...Array(monthCount).keys()].map((i) => {
      const currentMonthDate = addMonths(graphicalStartDate, i)
      const month = currentMonthDate.toLocaleString("default", { month: "short" })
      const year = currentMonthDate.getFullYear()

      return {
        downloadKey: currentMonthDate
          .toLocaleString("default", {
            month: "long",
            year: "numeric",
          })
          .replace(" ", "-"),
        label: `${capitalize(month)} ${year}`,
      }
    })
  }, [graphicalStartDate, graphicalEndDate, dataIsReady])

  // Build 2D map of warehouse -> sku -> suggested order data for SupplyPurchaseOrderDialog
  const skuSuggestedOrdersMap = useMemo<SkuSuggestedOrdersMap>(() => {
    const map: SkuSuggestedOrdersMap = new Map()
    if (stockoutTimes == undefined) return map

    for (const variant of rawVariants) {
      const flooredCells = flooredCellsByVariant.get(variant.variantId)
      const entries = flooredCells
        ? flooredCells.map((c) => ({
            skuId: c.skuId,
            warehouseId: c.warehouseId,
            suggestedOrder: c.suggestedOrder,
          }))
        : variant.skuIds.flatMap((skuId) => {
            const stockoutTime = stockoutTimes.get(skuId)
            if (!stockoutTime?.details) return []
            return Object.entries(stockoutTime.details)
              .filter(([warehouseId]) => isInBuyScope(warehouseId))
              .map(([warehouseId, detail]) => ({
                skuId,
                warehouseId,
                suggestedOrder: getCellBaseSuggestedOrder(
                  skuId,
                  warehouseId,
                  detail.suggestedOrder ?? 0,
                ),
              }))
          })

      for (const { skuId, warehouseId, suggestedOrder } of entries) {
        if (suggestedOrder <= 0) continue
        if (!map.has(warehouseId)) {
          map.set(warehouseId, new Map())
        }
        // Same detail the raw (un-floored) suggestion came from, so the pre-order floor's
        // contribution is just the difference. The base is the cell's own demand net of stock and
        // incoming — the same demand the "Demand" column shows — so the tooltip's terms are the
        // ones the number was actually built from, and a fashion buy carries no buffer.
        const key = demandCellKey(skuId, warehouseId)
        const detail = stockoutTimes.get(skuId)?.details?.[warehouseId]
        const inputs = suggestedOrderInputsByCell.get(key)
        const base = isFashion
          ? (suggestedOrderByCell.get(key) ?? 0)
          : (detail?.suggestedOrder ?? 0)
        const recordedInputs = isFashion && periodStartHasPassed
        const explanation: SuggestedOrderExplanation | undefined =
          detail && inputs
            ? {
                buffer: isFashion ? 0 : detail.buffer,
                stockAtArrival: recordedInputs
                  ? (actualStockAtStartBySkuWarehouse.get(skuId)?.get(warehouseId) ?? 0)
                  : detail.stockLevelAtArrival,
                demand: isFashion
                  ? (skuDemandBySkuWarehouseMap.get(skuId)?.get(warehouseId) ?? 0)
                  : inputs.demand,
                incomingInPeriod: recordedInputs
                  ? inputs.incomingInPeriod +
                    sumBy(
                      arrivedOrdersBySkuWarehouse.get(skuId)?.get(warehouseId) ?? [],
                      "quantity",
                    )
                  : inputs.incomingInPeriod,
                preorderFloorAdded: Math.max(0, suggestedOrder - base),
              }
            : undefined
        map.get(warehouseId)!.set(skuId, {
          suggestedOrder,
          variantId: variant.variantId,
          explanation,
        })
      }
    }
    return map
  }, [
    rawVariants,
    stockoutTimes,
    flooredCellsByVariant,
    suggestedOrderInputsByCell,
    isFashion,
    suggestedOrderByCell,
    skuDemandBySkuWarehouseMap,
    getCellBaseSuggestedOrder,
    isInBuyScope,
    periodStartHasPassed,
    actualStockAtStartBySkuWarehouse,
    arrivedOrdersBySkuWarehouse,
  ])

  const handleDownloadSpreadsheet = useCallback(
    async (fileFormat: "csv" | "xlsx") => {
      if (!dataIsReady || !filteredVariants.length) return
      setIsDownloadingCapacity(true)

      const warehousesMap = new Map(warehouses.map((warehouse) => [warehouse.id, warehouse.name]))
      const mutatedOrders = (closingStockData.orders ?? []).map((order) => ({
        ...order,
        warehouseId: warehousesMap.get(order.warehouseId) ?? "Unknown",
      }))
      const ordersBySkuAndWarehouse = groupBy(mutatedOrders, (x) => `${x.skuId}///${x.warehouseId}`)
      const monthColumnHeaders = dateColumns.map((data) => ({
        header: data.label,
        key: data.downloadKey,
      }))

      const planCollectionNames = (collections ?? []).map((c) => c.name)
      const vendorNameByVariantId = new Map(
        variants.map((v) => {
          const vendorId = resolveVariantVendorIdForPlan(v, planCollectionNames)
          const vendorName =
            vendorId === NO_VENDOR ? "" : (v.vendors.find((x) => x.id === vendorId)?.name ?? "")
          return [v.variantId, vendorName]
        }),
      )

      const gridApi = capacityGridRef.current?.api
      const isStructuralColumnId = (colId: string) =>
        colId.startsWith("calendar-") ||
        colId === "total" ||
        colId.endsWith("-snapshot") ||
        colId.startsWith("ag-Grid-AutoColumn") ||
        colId === ProductTableColumn.SKU ||
        colId === ProductTableColumn.Tags
      const addedColumns =
        gridApi
          ?.getColumns()
          ?.filter((col) => col.isVisible() && !isStructuralColumnId(col.getColId()))
          .map((col) => ({
            colId: col.getColId(),
            header: gridApi.getDisplayNameForColumn(col, null) || col.getColId(),
          })) ?? []
      const nodeByVariantId = new Map<string, IRowNode>()
      gridApi?.forEachLeafNode((node) => {
        const variantId = (node.data as IVariantSummary | undefined)?.variantId
        if (variantId) nodeByVariantId.set(variantId, node)
      })
      const getAddedColumnValues = (variantId: string | undefined) => {
        const node = variantId ? nodeByVariantId.get(variantId) : undefined
        return addedColumns.map((c) => {
          if (!node || !gridApi) return ""
          // Export numeric added columns as raw numbers (like the planning-month columns)
          // so they're easy to manipulate in Excel — not the on-screen abbreviated string
          // (e.g. "19,3K DKK"). Non-numeric columns keep their formatted display value.
          const raw = gridApi.getCellValue({ rowNode: node, colKey: c.colId, useFormatter: false })
          return typeof raw === "number"
            ? raw
            : (gridApi.getCellValue({ rowNode: node, colKey: c.colId, useFormatter: true }) ?? "")
        })
      }

      // Mirror the on-screen column order: identity fields, then user-added columns, then the
      // month columns, with the Total pinned last.
      const headers = [
        "SKU ID",
        "Warehouse",
        "Vendor",
        "Product ID",
        "Variant ID",
        ...addedColumns.map((c) => c.header),
        ...monthColumnHeaders.map((x) => x.header),
        "Total",
      ]

      // Enumerate every SKU shown in the grid across every warehouse so the export includes SKUs
      // with zero demand in all months — suppliers require the full SKU list in forecast plans.
      // Suggested orders only cover SKUs with demand, so combos without orders render as all-zero
      // rows. Union in any existing order keys (e.g. non-primary warehouses) so no row is dropped.
      const variantBySkuId = new Map<string, SupplyPlanVariant>()
      for (const variant of filteredVariants) {
        for (const skuId of variant.skuIds) variantBySkuId.set(skuId, variant)
      }
      const rowKeys = new Set<string>(Object.keys(ordersBySkuAndWarehouse))
      for (const skuId of variantBySkuId.keys()) {
        for (const warehouse of warehouses) rowKeys.add(`${skuId}///${warehouse.name}`)
      }

      // Prepare data rows
      const dataRows: any[] = []
      for (const key of rowKeys) {
        const orders = ordersBySkuAndWarehouse[key] ?? []
        const [skuId = "", warehouseName = ""] = key.split("///")
        const currentSku = variantBySkuId.get(skuId)
        const monthKeys = Object.fromEntries(monthColumnHeaders.map((x) => [x.key, 0]))
        orders.forEach((order) => {
          const month = new UTCDateMini(order.orderDate)
            .toLocaleString("default", {
              month: "long",
              year: "numeric",
            })
            .replace(" ", "-")

          if (!(month in monthKeys)) {
            return
          }

          monthKeys[month] = (monthKeys[month] ?? 0) + order.quantity
        })
        if (showCost) {
          const costPrice = currentSku?.costPrice ?? 0
          for (const monthKey of Object.keys(monthKeys)) {
            monthKeys[monthKey] = (monthKeys[monthKey] ?? 0) * costPrice
          }
        }
        const monthValues = Object.values(monthKeys)
        const total = monthValues.reduce((acc, value) => acc + (value ?? 0), 0)
        const row = [
          skuId,
          warehouseName,
          currentSku ? (vendorNameByVariantId.get(currentSku.variantId) ?? "") : "",
          currentSku?.productId ?? "",
          currentSku?.variantId ?? "",
          ...getAddedColumnValues(currentSku?.variantId),
          ...monthValues,
          total,
        ]
        dataRows.push(row)
      }

      // Build worksheet and workbook
      const worksheet = [headers, ...dataRows]
      let fileContent: ArrayBuffer | string
      let mimeType: string
      try {
        if (fileFormat === "xlsx") {
          const wb = utils.book_new()
          appendSheet(wb, "Supply Plan", worksheet)
          fileContent = write(wb, { bookType: "xlsx", type: "array" })
          mimeType = XLSX_CONTENT_TYPE
        } else {
          // CSV
          fileContent = utils.sheet_to_csv(sheetFromRows(worksheet))
          mimeType = "text/csv"
        }
        download(
          new Blob([fileContent], { type: mimeType }),
          `supply_plan-${formatDate(startOfTodayUTC())}.${fileFormat}`,
          mimeType,
        )
      } catch (error) {
        toast.error("Downloading supply plan failed ")
        throw error
      } finally {
        setTimeout(() => {
          setIsDownloadingCapacity(false)
        }, 500)
      }
    },
    [
      closingStockData.orders,
      variants,
      filteredVariants,
      warehouses,
      dateColumns,
      dataIsReady,
      showCost,
      collections,
    ],
  )

  const handleExportGreige = useCallback(() => {
    if (!greigeGridRef.current?.api) return
    setIsDownloadingGreige(true)
    try {
      greigeGridRef.current.api.exportDataAsExcel()
    } catch (error) {
      toast.error("Exporting greige plan failed unexpectedly with error: " + error)
    } finally {
      setTimeout(() => {
        setIsDownloadingGreige(false)
      }, 500)
    }
  }, [greigeGridRef])

  const handleSaveSnapshot = useCallback(
    (name: string) => {
      if (!dataIsReady || !purchasePlan) return

      const grouped = groupOrdersBySkuAndWarehouse(closingStockData.orders)
      const skus = variants.flatMap((v) =>
        v.skuIds.map((s) => ({
          skuId: s,
          leadTime: v.settings.totalLeadTimeDays,
          purchaseInterval: v.settings.purchaseIntervalDays,
          stockDetails: v.stockLevelDetails,
          stockOutInfo: v.stockOutInfoWarehouse,
        })),
      )

      saveSnapshot.mutate({
        purchasePlanId: purchasePlan.id,
        name,
        items: skus.flatMap((sku) =>
          warehouses.map((warehouse) => ({
            skuId: sku.skuId,
            warehouseId: warehouse.id,
            suggestedOrders: grouped[sku.skuId + "-" + warehouse.id] ?? [],
            safetyStockLevel:
              sku.stockOutInfo[warehouse.id]?.find((stockOut) => stockOut.skuId === sku.skuId)
                ?.buffer ?? 0,
            stockLevel:
              sku.stockDetails.find((s) => s.warehouseId === warehouse.id)?.skuStockLevels[
                sku.skuId
              ] ?? 0,
            leadTime: sku.leadTime,
            purchaseInterval: sku.purchaseInterval,
          })),
        ),
      })
    },
    [dataIsReady, purchasePlan, variants, warehouses, closingStockData.orders, saveSnapshot],
  )

  const quantityAdjustedOrders = useMemo(() => {
    // The server's order list is not OUT-aware, so OUT sizes are dropped here, before any scaling.
    const orders = outSkuIds.size
      ? closingStockData.orders?.filter((order) => !outSkuIds.has(order.skuId))
      : closingStockData.orders
    if (!orders?.length) return orders

    // Build reverse map: skuId -> variantId
    const skuToVariantMap = new Map<string, string>()
    for (const v of rawVariants) {
      for (const skuId of v.skuIds) {
        skuToVariantMap.set(skuId, v.variantId)
      }
    }

    // Collect variants with non-null quantity overrides
    const overrides = new Map<string, number>()
    for (const [variantId, qty] of variantQuantityMap) {
      if (qty != null) overrides.set(variantId, qty)
    }

    if (overrides.size === 0) return orders

    // Separate orders: overridden variants vs unaffected
    const ordersByVariant = new Map<string, ISuggestedOrder[]>()
    const adjusted: ISuggestedOrder[] = []

    for (const order of orders) {
      const vid = skuToVariantMap.get(order.skuId)
      if (!vid || !overrides.has(vid)) {
        adjusted.push(order)
        continue
      }
      if (!ordersByVariant.has(vid)) ordersByVariant.set(vid, [])
      ordersByVariant.get(vid)!.push(order)
    }

    const warehouseIds = warehouses.map((wh) => wh.id)
    const variantById = mapBy(rawVariants, "variantId")

    for (const [variantId, entered] of overrides) {
      if (entered <= 0) continue

      const variantOrders = ordersByVariant.get(variantId)
      const suggested = variantOrders ? sum(variantOrders.map((o) => o.quantity)) : 0
      const variant = variantById.get(variantId)
      const inSkuIds = variant?.skuIds.filter((skuId) => !outSkuIds.has(skuId)) ?? []
      if (inSkuIds.length === 0) continue

      if (suggested > 0 && entered <= suggested) {
        // Scale down proportionally based on existing sku/warehouse distribution
        const distribution = distributeQuantity(
          entered,
          variantOrders!.map((o, i) => ({ key: String(i), weight: o.quantity })),
        )
        for (const [idx, qty] of distribution) {
          const original = variantOrders![Number(idx)]!
          if (qty > 0) {
            adjusted.push({ ...original, quantity: qty })
          }
        }
      } else if (suggested > 0 && entered > suggested) {
        // Keep existing orders + distribute excess via warehouse weights and size split
        for (const order of variantOrders!) {
          adjusted.push(order)
        }
        const excess = entered - suggested
        const referenceDate = variantOrders!.reduce(
          (min, o) => (o.orderDate < min ? o.orderDate : min),
          variantOrders![0]!.orderDate,
        )
        adjusted.push(
          ...distributeAcrossWarehousesAndSkus(
            excess,
            inSkuIds,
            variantId,
            warehouseWeightsMap,
            sizeSplitWeightsMap,
            warehouseIds,
            referenceDate,
          ),
        )
      } else {
        // No existing orders: create from scratch using warehouse weights and size split
        adjusted.push(
          ...distributeAcrossWarehousesAndSkus(
            entered,
            inSkuIds,
            variantId,
            warehouseWeightsMap,
            sizeSplitWeightsMap,
            warehouseIds,
            calculationStartDate,
          ),
        )
      }
    }

    return adjusted
  }, [
    closingStockData.orders,
    outSkuIds,
    variantQuantityMap,
    rawVariants,
    warehouseWeightsMap,
    sizeSplitWeightsMap,
    calculationStartDate,
    warehouses,
  ])

  if (isLoading) return <LoadingOverlay />
  if (purchasePlan === undefined) return <NotFound />
  if (!dataIsReady) return <LoadingOverlay />

  return (
    <Module
      breadcrumb={
        <BreadcrumbWrapper
          items={[
            { label: "Procurement", href: "/procurement" },
            { label: "Supply Plans", href: "/procurement/supply-plan" },
            ...(() => {
              if (!collections || collections.length === 0) return []
              const groupIds = collections.map((c) => c.collectionGroupId).filter(Boolean)
              const uniqueGroupIds = [...new Set(groupIds)]
              if (
                uniqueGroupIds.length === 1 &&
                groupIds.length === collections.length &&
                uniqueGroupIds[0] != null
              ) {
                const groupName = collections.find((c) => c.groupName)?.groupName
                return [
                  {
                    label: groupName ?? "Collection",
                    href: `/procurement/supply-plan/collection/${uniqueGroupIds[0]}`,
                    search: { isGroup: true },
                  },
                ]
              }
              if (collections.length === 1 && !collections[0]!.collectionGroupId) {
                return [
                  {
                    label: collections[0]!.name,
                    href: `/procurement/supply-plan/collection/${collections[0]!.id}`,
                    search: { isGroup: false },
                  },
                ]
              }
              return []
            })(),
            { label: purchasePlan?.name ?? "" },
          ]}
        />
      }
      actions={
        purchasePlan.variantIds.length !== 0 ? (
          <div className="flex flex-row gap-2">
            {!purchasePlan.archived && (
              <BuySettingsDialog
                purchasePlanId={purchasePlan.id}
                storedSettings={purchasePlan.buySettings}
                collectionIds={purchasePlan.collectionIds}
              />
            )}
            {isFashion && <FashionBuyPlanExportMenu />}
            {collections && collections.length > 0 && (
              <CollectionInformationDialog collections={collections} />
            )}
            {isNOOSCollection && (
              <SaveSnapshotDialog
                onSave={(name) => handleSaveSnapshot(name)}
                disabled={!purchasePlan.variantIds.length}
              />
            )}
            {purchasePlan.collectionIds.length > 0 ? (
              <SyncPurchasePlanWithCollectionDialog
                purchasePlan={purchasePlan}
                isFashion={isFashion}
              />
            ) : (
              <>
                <ManagePurchasePlanDialog
                  purchasePlan={purchasePlan}
                  customTrigger={
                    <ActionButton
                      size="sm"
                      action={{
                        name: "Update supply plan",
                        icon: <PencilSimple />,
                      }}
                    />
                  }
                />
                <AddVariantsToPurchasePlanDialog purchasePlan={purchasePlan} />
              </>
            )}
          </div>
        ) : undefined
      }
      tabs={{ items: tabs, path: "/procurement/supply-plan/$purchasePlanId" }}
    >
      {closingStockData.isLoading ? (
        <LoadingOverlay />
      ) : (
        <SupplyPlanProvider
          value={{
            variants: filteredVariants,
            variantForecasts: alignedVariantForecasts,
            purchasePlan,
            isFashion,
            outSkuIds,
            orders: isFashion ? quantityAdjustedOrders : closingStockData.orders,
            closingStock: closingStockData.closingStock,
            closingStockWithOrders: closingStockData.closingStockWithOrders,
            dates: {
              calcStartDate: calculationStartDate,
              calcEndDate: calculationEndDate,
              graphStartDate: graphicalStartDate,
              graphEndDate: graphicalEndDate,
            },
            greigeGridRef,
            capacityGridRef,
            comparisonGroups,
            comparisonCollections,
            skuSuggestedOrdersMap,
            warehouseWeightsByVariant: warehouseWeightsMap,
            purchasedQuantityBySkuMap,
            purchasedQuantityBySkuWarehouseMap,
            draftQuantityBySkuWarehouseMap,
            erpQuantityBySkuWarehouseMap,
            purchaseOrdersBySkuWarehouseMap,
            projectedStockStartBySkuWarehouseMap,
            skuDemandBySkuWarehouseMap,
            periodStartHasPassed,
            periodEndHasPassed,
            actualStockAtEndBySkuWarehouse,
            arrivedOrdersBySkuWarehouse,
            showCost,
            setShowCost,
            comparisonSelect: (
              <div className="flex flex-row items-center justify-between gap-2">
                <span className="label-base">Compare to</span>
                <CollectionsSelect
                  selectedCollectionIds={comparisonCollectionIds}
                  onCollectionSelectChange={handleComparisonCollectionsChange}
                  selectedGroupIds={selectedGroupIds}
                  excludedCollectionIds={collectionIds}
                  excludeGroupsOnlyWhenFull
                  placeholder="Select collections..."
                  className="min-w-[185px]"
                  isMulti
                />
              </div>
            ),
            capacityExport: (
              <ActionButton
                size="sm"
                onClick={() => handleDownloadSpreadsheet("xlsx")}
                loading={isDownloadingCapacity}
                action={{ name: "Export capacity plan", icon: <Export /> }}
              />
            ),
            greigeExport: (
              <ActionButton
                size="sm"
                onClick={handleExportGreige}
                loading={isDownloadingGreige}
                action={{ name: "Export greige plan", icon: <Export /> }}
              />
            ),
          }}
        >
          {purchasePlan.variantIds.length === 0 ? (
            <div className="flex flex-col gap-2 items-center justify-center h-full">
              <img
                className="w-[220px] h-[160px]"
                src="/no_collections.png"
                alt="Explanation of supply plan"
              />
              <p className="heading-sm mt-4">No variants added</p>
              <p className="paragraph-base text-fg-subtle text-center mb-4 w-[220px]">
                You have not added any variants to this supply plan.
              </p>
              <AddVariantsToPurchasePlanDialog purchasePlan={purchasePlan} />
            </div>
          ) : (
            <Outlet />
          )}
        </SupplyPlanProvider>
      )}
    </Module>
  )
}
